import * as React from 'react';

import type { AnswerPayload } from '@/domains/survey';
import { NO_ANSWER, answerAddress, payloadText } from '../answer-text';
import type { ITimelineRow } from '../api/oversight-contracts';
import { absent } from './account-facts';
import { withBatch } from './history-batch';
import type { IHistoryRow } from './history-popover';

/**
 * A response timeline row, rendered — one rendering for the Timeline tab and a question's history
 * popover on the Answers tab (SR-222's F4, the plan's R8), since two screens drawing one row two
 * ways would disagree about the same change.
 *
 * A row's values are strings of three kinds, and each is drawn for what it is:
 *
 * - **native** — canonical JSON. An answer's payload (`{"t":"text","v":…}`) is drawn as the
 *   oversight detail draws a payload (`payloadText`); anything else a native row carries (a status
 *   word, a JSON that does not parse) is drawn as sent, since a guess at its shape would hide what
 *   survey-api wrote.
 * - **legacy** — the old platform's text, verbatim and unnormalised: the ETL's ruling keeps that log
 *   as it was, and this side must not disagree with the table it reads.
 * - **internal** — a staff field's payload, drawn as sent: its words belong to the field's type
 *   (`internal-field-values.ts`), which the timeline does not carry, and reading it as an answer's
 *   payload would draw a rich text's markup or an option code as if it were an answer. One imported
 *   from Salesforce is `{"v": …}` like any other since survey-api's block 9 lifted the envelope's
 *   Salesforce members into `salesforce`.
 * - **salesforce** — Salesforce's text, verbatim and never parsed: it is not canonical JSON, and a
 *   picklist's value is Salesforce's label, not an option code of ours.
 *
 * Who is named by the account's email with its role beside it (or the old platform's user id, or
 * the Salesforce user — every account member of a Salesforce row is null), in one function for the
 * table cell, the popover and a correction's conflict sentence: "an account since removed" where
 * survey-api sends a null email for an account that acted, and the account id, as before, where an
 * older API sends no `actorEmail` at all.
 *
 * **A sync change is hidden by default, on this side** (`isSyncChange`): survey-api marks the rows
 * the legacy platform's sync wrote to Salesforce — the advisor's own saves, which the `legacy`
 * source already shows — and filters none, so the Timeline tab and a question's history take them
 * off the page they read behind one switch, "Hide sync changes", and say how many they took. It
 * filters a page, never the history: `total` and the pager stay survey-api's.
 */
export function timelineValue(row: ITimelineRow, value: Nullable<string>): string {
  if (value === null) return NO_ANSWER;
  if (row.source !== 'native') return value;
  const payload = parsePayload(value);
  return payload ? payloadText(payload) : value;
}

/** A canonical answer payload, or `null` for a native value that is not one. */
function parsePayload(value: string): Nullable<AnswerPayload> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }
  return typeof parsed === 'object' &&
    parsed !== null &&
    typeof (parsed as { t?: unknown }).t === 'string'
    ? (parsed as AnswerPayload)
    : null;
}

/** The switch's words, on the Timeline tab and a question's history alike. */
export const HIDE_SYNC_LABEL = 'Hide sync changes';

/** A row the legacy platform's sync wrote to Salesforce — the advisor's own save, already shown by
 *  the `legacy` source. Only survey-api's flag says so. */
export function isSyncChange(row: ITimelineRow): boolean {
  return row.salesforce?.legacySync === true;
}

/** What the row changed, by name: the question's usage code, the old platform's column, the staff
 *  field's code, or — for a Salesforce field whose target the response's version lacks — the
 *  field's label, else its API name. */
export function timelineField(row: ITimelineRow): Nullable<string> {
  return (
    row.usage ??
    row.legacyField ??
    row.internalField ??
    row.salesforce?.fieldLabel ??
    row.salesforce?.field ??
    null
  );
}

/**
 * Words for the native kinds SR-416 added to a response's history (F6) — words, not a mirror:
 * survey-api filters no kind, and one missing here is drawn as spelled. `response_reopened` reads
 * its reason off the row's stored value (`{reason: "firm_change" | "path_change", …}`), which the
 * row's values still draw whole. A `status_change` is a recycle bin move (SR-470's D8), read off
 * its values — the wire's statuses as JSON strings: to `recycled` a move, from it a restore, any
 * other pair drawn as spelled. A `response_submitted` says who submitted it where the acting
 * account was not the participant's own (SR-487 ruling 19): its `{relation}`, null for staff.
 */
const EVENT_WORDS: Readonly<Record<string, string>> = {
  response_reopened: 'Reopened',
  path_changed: 'Moved to another path',
  member_lookup: 'Team member looked up',
  respondent_lookup: 'CRD looked up'
};

const SUBMITTED_BY_WORDS: Readonly<Record<string, string>> = {
  assistant: 'Submitted by an assistant',
  contact: 'Submitted by a point of contact',
  member: 'Submitted by a listed member'
};

const REOPEN_REASON_WORDS: Readonly<Record<string, string>> = {
  firm_change: 'Reopened — the firm changed',
  path_change: 'Reopened — the path changed'
};

/** A native row's kind in words, or `null` for a kind this app has none for (drawn as spelled). */
export function timelineEventWords(row: ITimelineRow): Nullable<string> {
  if (row.source !== 'native' || !row.eventType) return null;
  if (row.eventType === 'status_change') {
    if (jsonString(row.newValue) === 'recycled') return 'Moved to the recycle bin';
    if (jsonString(row.oldValue) === 'recycled') return 'Restored from the recycle bin';
    return null;
  }
  if (row.eventType === 'response_reopened') {
    const reason = storedMember(row.newValue, 'reason');
    if (typeof reason === 'string' && REOPEN_REASON_WORDS[reason]) {
      return REOPEN_REASON_WORDS[reason];
    }
  }
  if (row.eventType === 'response_submitted') {
    const relation = storedMember(row.newValue, 'relation');
    return typeof relation === 'string' ? (SUBMITTED_BY_WORDS[relation] ?? null) : null;
  }
  return EVENT_WORDS[row.eventType] ?? null;
}

/** A member of a native row's stored JSON object — `undefined` where the value is none, or is not
 *  JSON: such a value says nothing of it, and the kind's own word stands. */
function storedMember(value: Nullable<string>, member: string): unknown {
  if (value === null) return undefined;
  try {
    const stored = JSON.parse(value) as unknown;
    return stored !== null && typeof stored === 'object'
      ? (stored as Record<string, unknown>)[member]
      : undefined;
  } catch {
    return undefined;
  }
}

/** A native value that is a JSON string, unquoted — `null` for anything else. */
function jsonString(value: Nullable<string>): Nullable<string> {
  if (value === null) return null;
  try {
    const parsed = JSON.parse(value) as unknown;
    return typeof parsed === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

/** What happened: the event type — in words where it has some, the stored kind beside — else the
 *  system the row came from — a legacy or Salesforce row carries none. A sync change says so. */
export function timelineEvent(row: ITimelineRow): string {
  const words = timelineEventWords(row);
  if (words && row.eventType) return `${words} · ${row.eventType}`;
  if (row.eventType) return row.eventType;
  if (row.source === 'salesforce') return isSyncChange(row) ? 'Salesforce · sync' : 'Salesforce';
  return 'legacy platform';
}

/** The words for an account that acted and is gone — the Activity's. */
const REMOVED_ACCOUNT = 'an account since removed';

/**
 * The account that made the change, by name: its email; its id where survey-api sent no
 * `actorEmail` member (absent is not null); `null` for an account since removed; `undefined`
 * where no account acted.
 */
function accountName(row: ITimelineRow): Nullable<string> | undefined {
  if (row.actorEmail) return row.actorEmail;
  if (!row.actorAccountId) return undefined;
  return row.actorEmail === null ? null : row.actorAccountId;
}

/** A Salesforce row whose user the archive did not record — survey-api sends the id when it has
 *  no name, so this is a history row with no `CreatedById` at all. */
const UNNAMED_SALESFORCE_USER = 'not recorded';

/** Who made the change, in one line — the conflict sentence's "changed by …" — or `null` where a
 *  row names nobody. */
export function timelineActorText(row: ITimelineRow): Nullable<string> {
  if (row.legacyActor) return `legacy user ${row.legacyActor}`;
  if (row.salesforce) {
    return `Salesforce user ${row.salesforce.actor ?? UNNAMED_SALESFORCE_USER}`;
  }
  const name = accountName(row);
  if (name === undefined) return null;
  const role = row.actorRole ?? 'account';
  return name === null ? `${REMOVED_ACCOUNT} (${role})` : `${role} ${name}`;
}

/** Who made the change, as a table cell: the account's email, its role under it. */
export function TimelineActor({ row }: { row: ITimelineRow }) {
  if (row.legacyActor) return <>legacy user {row.legacyActor}</>;
  if (row.salesforce) {
    return (
      <>
        {row.salesforce.actor ?? absent(UNNAMED_SALESFORCE_USER)}
        <span className="block">Salesforce user</span>
      </>
    );
  }
  const name = accountName(row);
  if (name === undefined) return <>{NO_ANSWER}</>;
  return (
    <>
      {name === null ? absent(REMOVED_ACCOUNT) : name}
      {row.actorRole && <span className="block">{row.actorRole}</span>}
    </>
  );
}

/**
 * One row of a question's history (`HistoryPopover`): what happened (`timelineEvent`) and where, at
 * the answer's address, since a grid's, a repeat's or a roster's rows of one question come back
 * together; the values by `timelineValue`; the reason a correction carries, and the list batch it
 * was made in (SR-403, `withBatch`). A sync change is `hideable`, for the popover's switch.
 */
export function toHistoryRow(row: ITimelineRow): IHistoryRow {
  const address = answerAddress(row);
  return {
    key: `${row.source}-${row.id}`,
    at: row.occurredAt,
    who: <TimelineActor row={row} />,
    hideable: isSyncChange(row),
    what: withBatch(
      <>
        <span className="text-xs">{timelineEvent(row)}</span>
        {address && (
          <span className="block text-[0.6875rem] text-muted-foreground">{address}</span>
        )}
      </>,
      row.batch
    ),
    from: (
      <span className="break-words text-muted-foreground">{timelineValue(row, row.oldValue)}</span>
    ),
    to: <span className="break-words">{timelineValue(row, row.newValue)}</span>,
    reason: row.reason
  };
}
